import * as React from 'react'
import { Percent } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { FormField } from '@/components/templates/entity-form'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { getJSON, postJSON } from '@/lib/api'
import { apiFieldMessages } from '@/lib/contacts/errors'
import { useViewOnly } from '@/components/templates/app-shell'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

export interface TaxRate {
  id: number
  name: string
  percentage: string
  kind: 'standard' | 'reduced' | 'zero_rated' | 'exempt' | 'outside_scope'
}

export interface TaxRatesResponse {
  tax_rates: TaxRate[]
}

interface TaxRateResponse {
  tax_rate: TaxRate
}

export function TaxSettingsPage() {
  const viewOnly = useViewOnly()
  const queryClient = useQueryClient()
  const [form, setForm] = React.useState({
    name: '',
    percentage: '',
    kind: 'standard' as TaxRate['kind'],
  })
  const ratesQuery = useQuery({
    queryKey: ['tax-rates'],
    queryFn: () => getJSON<TaxRatesResponse>('/api/v1/tax_rates'),
    retry: false,
  })
  const createRate = useMutation({
    mutationFn: () =>
      postJSON<TaxRateResponse>('/api/v1/tax_rates', {
        tax_rate: { name: form.name, percentage: form.percentage, kind: form.kind },
      }),
    onSuccess: async () => {
      setForm({ name: '', percentage: '', kind: 'standard' })
      await queryClient.invalidateQueries({ queryKey: ['tax-rates'] })
    },
  })
  const rates = ratesQuery.data?.tax_rates ?? []

  return (
    <div className="mx-auto w-full max-w-[1200px] px-6 py-6">
      <header className="pb-4">
        <div className="flex items-center gap-3">
          <h1 className="text-heading-sm font-semibold text-foreground">Taxes</h1>
          {viewOnly && <Badge variant="info">View only</Badge>}
        </div>
      </header>

      <Card className="max-w-2xl overflow-hidden">
        <CardContent className="divide-y divide-border p-0">
          {!viewOnly && (
            <TaxRateForm
              form={form}
              error={createRate.error}
              pending={createRate.isPending}
              onChange={setForm}
              onSubmit={() => createRate.mutate()}
            />
          )}
          <section>
            <h2 className="p-4 pb-3 text-body font-semibold text-foreground">Tax Rates</h2>
            {ratesQuery.isLoading ? (
              <p className="px-4 pb-4 text-body-sm text-muted-foreground" role="status">
                Loading Tax Rates...
              </p>
            ) : ratesQuery.isError ? (
              <div className="px-4 pb-4">
                <Alert>Tax Rates unavailable</Alert>
              </div>
            ) : (
              <TaxRateTable rates={rates} />
            )}
          </section>
        </CardContent>
      </Card>
    </div>
  )
}

function TaxRateForm({
  form,
  error,
  pending,
  onChange,
  onSubmit,
}: {
  form: { name: string; percentage: string; kind: TaxRate['kind'] }
  error: Error | null
  pending: boolean
  onChange: React.Dispatch<
    React.SetStateAction<{ name: string; percentage: string; kind: TaxRate['kind'] }>
  >
  onSubmit: () => void
}) {
  return (
    <section className="p-4">
      <h2 className="flex items-center gap-2 pb-3 text-body font-semibold">
        <Percent className="h-4 w-4 text-primary" strokeWidth={1.5} />
        Create Tax Rate
      </h2>
      <form
        className="grid items-end gap-3 sm:grid-cols-[minmax(0,1fr)_10rem_auto]"
        onSubmit={(event) => {
          event.preventDefault()
          onSubmit()
        }}
      >
        <FormField
          label="Tax Rate name"
          htmlFor="tax-rate-name"
          error={apiFieldMessages(error, 'name')[0]}
        >
          <Input
            id="tax-rate-name"
            value={form.name}
            onChange={(event) => onChange({ ...form, name: event.target.value })}
          />
        </FormField>
        <FormField
          label="Percentage"
          htmlFor="tax-rate-percentage"
          error={apiFieldMessages(error, 'percentage')[0]}
        >
          <Input
            id="tax-rate-percentage"
            inputMode="decimal"
            value={form.percentage}
            onChange={(event) => onChange({ ...form, percentage: event.target.value })}
          />
        </FormField>
        <FormField label="Tax kind" htmlFor="tax-rate-kind">
          <Select
            value={form.kind}
            onValueChange={(kind) => onChange({ ...form, kind: kind as TaxRate['kind'] })}
          >
            <SelectTrigger id="tax-rate-kind">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {['standard', 'reduced', 'zero_rated', 'exempt', 'outside_scope'].map((kind) => (
                <SelectItem key={kind} value={kind}>
                  {kind.replace('_', ' ')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <Button type="submit" disabled={pending}>
          {pending ? 'Creating...' : 'Create Tax Rate'}
        </Button>
      </form>
    </section>
  )
}

function TaxRateTable({ rates }: { rates: TaxRate[] }) {
  if (rates.length === 0) {
    return <p className="px-4 pb-4 text-body-sm text-muted-foreground">No Tax Rates yet</p>
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead className="text-right">Percentage</TableHead>
          <TableHead>Kind</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rates.map((rate) => (
          <TableRow key={rate.id}>
            <TableCell className="font-medium">{rate.name}</TableCell>
            <TableCell className="text-right tabular-nums">{rate.percentage}%</TableCell>
            <TableCell>{(rate.kind ?? 'standard').replace('_', ' ')}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
